import { Injectable, inject } from '@angular/core';
import { HttpRequestService } from '../GlobalHttpRequest/httpRequest.service';
import { environment } from '@core/environments/environments';

/** Permiso del catálogo que habilita el Análisis Económico (Inicio → tableros estadísticos). */
export const FINANCIAL_PERMISSION_LABEL = 'costos vs utilidad';

export type FinancialDateBasis = 'reservation' | 'travel';
export type FinancialStatusFilter = 'all' | 'verified' | 'pending_verification';
export type FinancialGranularity = 'day' | 'week' | 'month';

export interface FinancialTotals {
  count: number;
  sales: number;
  costs: number;
  commissions: number;
  /** Venta − costo − comisión. */
  profit: number;
  tax: number;
  netProfit: number;
  /** Utilidad neta / venta, en %. */
  margin: number;
}

export interface FinancialReservationRow {
  id: number;
  code: string;
  client: string;
  /** Tipo y número de documento del cliente; vacíos si no los tiene. */
  clientDocumentType: string;
  clientDocument: string;
  advisorId: number | null;
  advisor: string;
  city: string;
  reservationDate: string | null;
  travelDate: string | null;
  status: string;
  sales: number;
  /** Costo aplicado: el configurado en los productos, o el "Vr. compra" donde se registró. */
  costs: number;
  costByCategory: Record<string, number>;
  /** Venta por tipo de servicio, prorrateada al valor final de la reservación. */
  salesByCategory: Record<string, number>;
  costDetail: Array<{
    category: string;
    configured: number;
    /** "Vr. compra" del tablero; si existe reemplaza al configurado. */
    override: number | null;
    applied: number;
  }>;
  /** Servicios sin costo configurado (producto borrado o sin datos del proveedor). */
  unresolvedCostItems: number;
  commission: number;
  profit: number;
  tax: number;
  netProfit: number;
  margin: number;
}

export interface FinancialTrendPoint {
  key: string;
  label: string;
  sales: number;
  costs: number;
  commissions: number;
  tax: number;
  netProfit: number;
}

export interface FinancialCategorySummary {
  category: string;
  /** Reservas que incluyen este tipo de servicio. */
  reservations: number;
  sales: number;
  costs: number;
  /** Venta − costo (la comisión y el impuesto son de la reserva completa). */
  grossProfit: number;
  margin: number;
}

export interface FinancialAdvisorSummary {
  advisorId: number | null;
  advisor: string;
  count: number;
  sales: number;
  costs: number;
  commissions: number;
  netProfit: number;
  margin: number;
}

export interface FinancialAnalysis {
  range: {
    from: string;
    to: string;
    dateBasis: FinancialDateBasis;
    status: FinancialStatusFilter;
    advisorId: number | null;
    granularity: FinancialGranularity;
  };
  previousRange: { from: string; to: string };
  taxPercentage: number;
  summary: FinancialTotals;
  previous: FinancialTotals;
  trend: FinancialTrendPoint[];
  /** Mismos cortes que `trend`, del periodo anterior: el punto i se compara con el i. */
  previousTrend: FinancialTrendPoint[];
  byCategory: FinancialCategorySummary[];
  byAdvisor: FinancialAdvisorSummary[];
  advisors: Array<{ id: number; name: string }>;
  reservations: FinancialReservationRow[];
}

export interface FinancialReportFile {
  fileName: string;
  mimeType: string;
  contentBase64: string;
}

export interface FinancialAnalysisFilters {
  from: string;
  to: string;
  dateBasis: FinancialDateBasis;
  status: FinancialStatusFilter;
  advisorId: number | null;
}

@Injectable({ providedIn: 'root' })
export class FinancialAnalysisApiService {
  private readonly http = inject(HttpRequestService);
  private readonly base = `${environment.baseUrl}/financial-analysis`;

  async getAnalysis(filters: FinancialAnalysisFilters): Promise<FinancialAnalysis> {
    return this.http.makeRequest<FinancialAnalysis, object>(
      `${this.base}?${this.toParams(filters)}`,
      {},
      'GET',
    );
  }

  /** Excel del reporte (una hoja por sección), con los mismos filtros. */
  async exportExcel(filters: FinancialAnalysisFilters): Promise<FinancialReportFile> {
    return this.http.makeRequest<FinancialReportFile, object>(
      `${this.base}/export?${this.toParams(filters)}`,
      {},
      'GET',
    );
  }

  private toParams(filters: FinancialAnalysisFilters): string {
    const params = new URLSearchParams({
      from: filters.from,
      to: filters.to,
      dateBasis: filters.dateBasis,
      status: filters.status,
    });
    if (filters.advisorId != null) params.set('advisorId', String(filters.advisorId));
    return params.toString();
  }
}
